import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, MapPin, User, Lock, Search, Clock, CreditCard, Banknote, ChevronDown, Loader, CheckCircle, X, Plus, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import { isStoreOpen, getTradingWindowForDate } from '../utils/storeHours';

const TENANT_STORAGE_SCOPE = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : 'server';
const CHECKOUT_STORAGE_KEY = `checkoutDeliveryState:${TENANT_STORAGE_SCOPE}`;
const CHECKOUT_MODE_KEY = `checkoutMode:${TENANT_STORAGE_SCOPE}`;

// Generate time slots based on interval (in minutes)
const generateTimeSlots = ({ openingHours, specialHours, tradingStatus, intervalMinutes = 30, leadTimeMinutes = 30 }) => {
  if (!openingHours) return [];

  const now = new Date();
  const serverWindow = tradingStatus?.scheduleOpen && tradingStatus?.opensAt && tradingStatus?.closesAt
    ? { opensAt: new Date(tradingStatus.opensAt), closesAt: new Date(tradingStatus.closesAt) }
    : null;
  const localWindow = serverWindow || getTradingWindowForDate(openingHours, specialHours, now);
  if (!localWindow) return [];
  const openDate = localWindow.opensAt;
  const closeDate = localWindow.closesAt;

  const earliest = new Date(now.getTime() + leadTimeMinutes * 60000);
  let start = earliest > openDate ? earliest : openDate;

  const mins = start.getMinutes();
  const remainder = mins % intervalMinutes;

  if (remainder !== 0) {
    start.setMinutes(mins + (intervalMinutes - remainder));
  }

  start.setSeconds(0, 0);

  const slots = [];

  while (start <= closeDate) {
    const hh = start.getHours().toString().padStart(2, '0');
    const mm = start.getMinutes().toString().padStart(2, '0');

    slots.push(`${hh}:${mm}`);
    start = new Date(start.getTime() + intervalMinutes * 60000);
  }

  return slots;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartSubtotal, orderType, user, authLoading, setCurrentOrder, clearCart } = useApp();
  const [activeTab, setActiveTab] = useState(orderType || 'delivery');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [scheduledType, setScheduledType] = useState('asap');
  const [scheduledTime, setScheduledTime] = useState('');
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const hadCartBefore = useRef(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
useEffect(() => {
  if (cart.length > 0) {
    hadCartBefore.current = true;
  }
}, [cart]);

  useEffect(() => {
  if (cart.length === 0 && hadCartBefore.current && !isPlacingOrder) {
    navigate('/', { replace: true });
  }
}, [cart, navigate, isPlacingOrder]);
  // Check for cancelled payment from Stripe and restore form data
  useEffect(() => {
    const handleCancelledPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('payment') === 'cancelled') {
        setPaymentCancelled(true);
        const orderId = params.get('order');
        const cancelToken = params.get('cancelToken');

        // Stripe's Back link means this checkout is abandoned. Expire it now so
        // browser Forward cannot reopen a payable session.
        if (orderId && cancelToken) {
          try {
            await api.cancelCheckoutSession(orderId, cancelToken);
          } catch (e) {
            console.error('Failed to close cancelled payment session:', e);
          }
        }

        // Restore form data from localStorage
        const savedFormData = localStorage.getItem('checkoutFormData');
        if (savedFormData) {
          try {
            const formData = JSON.parse(savedFormData);
            setGuestInfo(formData.guestInfo || { name: '', email: '', phone: '' });
            setDeliveryAddress(formData.deliveryAddress || { line1: '', line2: '', city: '', postcode: '' });
            setNotes(formData.notes || '');
            setActiveTab(formData.orderType || 'delivery');
            setScheduledType(formData.scheduledType || 'asap');
            setScheduledTime(formData.scheduledTime || '');
          } catch (e) {
            console.error('Failed to restore form data:', e);
          }
        }

        // Remove the signed cancellation token from the visible browser URL.
        window.history.replaceState({}, '', '/checkout');
      }

    };

    handleCancelledPayment();
  }, []);

  // Coupon
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'delivery' || appliedCoupon?.type !== 'free_delivery') return;
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('Free delivery coupons can only be used on delivery orders.');
  }, [activeTab, appliedCoupon]);

  // Address
  const [postcodeInput, setPostcodeInput] = useState('');
  const [postcodeSearching, setPostcodeSearching] = useState(false);
  const [postcodeResult, setPostcodeResult] = useState(null);
  const [postcodeError, setPostcodeError] = useState('');
  const [manualEntry, setManualEntry] = useState(false);
  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState({ line1: '', line2: '', city: '', postcode: '' });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedAddressesLoading, setSavedAddressesLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressEditorOpen, setAddressEditorOpen] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState(() => user ? 'account' : sessionStorage.getItem(CHECKOUT_MODE_KEY));

  // Guest
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '', phone: '' });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const checkoutSectionClass = 'bg-white border-b border-gray-100 overflow-hidden sm:rounded-2xl sm:border sm:border-gray-200';
  const checkoutHeaderClass = 'px-5 pt-5 pb-2 sm:p-4 sm:border-b sm:border-gray-100';
  const checkoutBodyClass = 'px-5 pt-3 pb-5 sm:p-4';

  useEffect(() => {
    if (user) {
      setCheckoutMode('account');
      setSavedAddressesLoading(true);
      api.getCustomerAddresses()
        .then(async addresses => {
          const unique = Array.from(new Map((addresses || []).map(address => [
            `${address.line1}|${address.line2 || ''}|${address.city}|${String(address.postcode).replace(/\s/g, '').toUpperCase()}`.toLowerCase(),
            address
          ])).values());
          setSavedAddresses(unique);
          if (unique.length === 1) {
            await selectSavedAddress(unique[0]);
          }
        })
        .catch(() => setSavedAddresses([]))
        .finally(() => setSavedAddressesLoading(false));
    } else {
      setSavedAddresses([]);
      setSelectedAddressId(null);
    }
  // The address selector intentionally reloads only when account identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Restaurant info for service charge and store hours
  const [restaurant, setRestaurant] = useState(null);
  useEffect(() => {
    let active = true;
    const refreshAvailability = async () => {
      try {
        const rest = await api.getRestaurant();
        if (!active) return;
        setRestaurant(rest);
        const resolvedOpen = rest?.tradingStatus?.isOpen;
        const storeOpen = typeof resolvedOpen === 'boolean'
          ? resolvedOpen
          : !rest.pauseIncomingOrders && isStoreOpen(rest.openingHours, rest.specialHours);
        if (!storeOpen) {
          // The shared MenuPage explains the current pause/closure reason.
          navigate('/', { replace: true });
        }
      } catch (_) {
        // Keep the current checkout visible during a temporary network failure.
      }
    };
    refreshAvailability();
    const intervalId = window.setInterval(refreshAvailability, 15000);
    window.addEventListener('focus', refreshAvailability);
    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshAvailability);
    };
  }, [navigate]);

    useEffect(() => {
  if (restaurant?.name) {
    document.title = `Checkout - ${restaurant.name}`;
  } else {
    document.title = "Checkout";
  }
}, [restaurant]);

  useEffect(() => {
  // Remove the old global key which could retain a previous customer's full
  // address. Only non-personal checkout pricing/mode state is restored now.
  localStorage.removeItem('checkoutDeliveryState');
  const savedCheckoutState = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
  if (!savedCheckoutState) return;

  try {
    const parsed = JSON.parse(savedCheckoutState);

    if (parsed.activeTab) setActiveTab(parsed.activeTab);
    if (parsed.deliveryFeeInfo) setDeliveryFeeInfo(parsed.deliveryFeeInfo);
  } catch (e) {
    console.error('Failed to restore checkout delivery state:', e);
  }
}, []);

useEffect(() => {
  const checkoutState = {
    activeTab,
    deliveryFeeInfo
  };

  sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkoutState));
}, [activeTab, deliveryFeeInfo]);

  const serviceCharge = restaurant?.serviceCharge || 0;
  const defaultDeliveryFee = restaurant?.deliveryFee ?? 2.50;
const scheduleInterval = restaurant?.scheduleInterval || 30;

const getMinimumLeadTime = (timeString, fallback) => {
  const match = String(timeString || '').match(/\d+/);
  return match ? parseInt(match[0], 10) : fallback;
};

const leadTimeMinutes =
  activeTab === 'pickup'
    ? getMinimumLeadTime(restaurant?.pickupTime, 15)
    : getMinimumLeadTime(restaurant?.deliveryTime, 30);

const timeSlots = generateTimeSlots({
  openingHours: restaurant?.openingHours,
  specialHours: restaurant?.specialHours,
  tradingStatus: restaurant?.tradingStatus,
  intervalMinutes: scheduleInterval,
  leadTimeMinutes
});

  // Delivery fee from zone check or default
  const currentDeliveryFee = activeTab === 'delivery'
    ? (deliveryFeeInfo?.fee !== undefined ? deliveryFeeInfo.fee : defaultDeliveryFee)
    : 0;
  const discount = appliedCoupon?.type === 'free_delivery'
    ? currentDeliveryFee
    : (appliedCoupon?.discount || 0);
  const total = cartSubtotal + currentDeliveryFee + serviceCharge - discount;

  const selectSavedAddress = async (address) => {
    setSelectedAddressId(address.id);
    setAddressEditorOpen(false);
    setDeliveryAddress({
      line1: address.line1,
      line2: address.line2 || '',
      city: address.city,
      postcode: address.postcode,
      latitude: address.latitude,
      longitude: address.longitude
    });
    setPostcodeInput(address.postcode);
    setPostcodeError('');
    setPostcodeResult(null);
    setManualEntry(false);
    try {
      const zoneCheck = await api.checkDeliveryZone(address.postcode);
      if (!zoneCheck.deliverable) {
        setDeliveryFeeInfo(null);
        setPostcodeError('This saved address is outside the current delivery area. Please choose another address or Pickup.');
        return;
      }
      setDeliveryFeeInfo(zoneCheck);
    } catch (_) {
      setDeliveryFeeInfo({ deliverable: true, fee: defaultDeliveryFee });
    }
  };

  const startNewAddress = () => {
    setSelectedAddressId(null);
    setAddressEditorOpen(true);
    setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
    setPostcodeInput('');
    setPostcodeResult(null);
    setDeliveryFeeInfo(null);
    setPostcodeError('');
    setManualEntry(false);
  };

  const removeSavedAddress = async (event, addressId) => {
    event.stopPropagation();
    try {
      await api.deleteCustomerAddress(addressId);
      setSavedAddresses(current => current.filter(address => address.id !== addressId));
      if (selectedAddressId === addressId) startNewAddress();
    } catch (_) {
      setError('We could not remove that address. Please try again.');
    }
  };

  // Postcode lookup
  const handlePostcodeSearch = async () => {
    const pc = postcodeInput.trim();
    if (!pc) return;
    setPostcodeSearching(true);
    setPostcodeError('');
    setPostcodeResult(null);
    setDeliveryFeeInfo(null);

    try {
      // Lookup address info
      const result = await api.lookupPostcode(pc);
      setPostcodeResult(result);

      // Check delivery zone
      try {
        const zoneCheck = await api.checkDeliveryZone(pc);
        setDeliveryFeeInfo(zoneCheck);
        if (!zoneCheck.deliverable) {
          setPostcodeError('We do not deliver to this postcode. Please choose Pickup instead or enter a different postcode.');
          setPostcodeResult(null); // Don't show addresses if not deliverable
          return; // Exit early
        }
      } catch {
        // No zones configured, use default fee
        setDeliveryFeeInfo({ deliverable: true, fee: defaultDeliveryFee });
      }

      // Auto-populate postcode and town
      setDeliveryAddress(prev => ({
        ...prev,
        postcode: result.postcode,
        city: result.town || '',
        latitude: result.latitude,
        longitude: result.longitude
      }));
    } catch (err) {
      setPostcodeError('Postcode not found. Please check and try again.');
    } finally {
      setPostcodeSearching(false);
    }
  };

  const handleSelectAddress = (line1) => {
    setDeliveryAddress(prev => ({ ...prev, line1 }));
    setPostcodeResult(null);
  };

  // Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      // Send cart items for BOGO validation
      const items = cart.map(item => ({
        itemId: item.id,
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        category: item.category
      }));
      const result = await api.validateCoupon(
        couponCode.trim(),
        cartSubtotal,
        items,
        activeTab,
        currentDeliveryFee
      );
      setAppliedCoupon(result);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    
    if (scheduledType === 'later' && !scheduledTime) {
  setError('Please select a scheduled time');
  return;
}

if (scheduledType === 'later' && !timeSlots.includes(scheduledTime)) {
  setError('Selected scheduled time is no longer available. Please choose another time.');
  return;
}

    // Validation: Check customer info if not logged in
    if (!user) {
      if (!guestInfo.name || !guestInfo.email || !guestInfo.phone) {
        setError('Please provide your name, email, and phone number');
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestInfo.email)) {
        setError('Please provide a valid email address');
        return;
      }
    }
    
    // Validation: Check delivery address if delivery is selected
    if (activeTab === 'delivery') {
      if (!deliveryAddress.line1 || !deliveryAddress.city || !deliveryAddress.postcode) {
        setError('Please provide a complete delivery address');
        return;
      }
      if (postcodeError || deliveryFeeInfo?.deliverable === false) {
        setError('Please choose an address inside the current delivery area');
        return;
      }
    }
    
    setLoading(true);
    setError('');
    try {
      // Save form data to localStorage before creating order (in case of Stripe redirect)
      const formData = {
        guestInfo,
        deliveryAddress,
        notes,
        orderType: activeTab,
        scheduledType,
        scheduledTime
      };
      localStorage.setItem('checkoutFormData', JSON.stringify(formData));

      const orderData = {
        type: activeTab,
        paymentMethod,
        items: cart.map(item => ({
          itemId: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          selectedOptions: item.selectedOptions || {},
          promotionReward: item.promotionReward || false,
          promotionId: item.promotionId || null,
          rewardId: item.rewardId || null
        })),
        deliveryAddress: activeTab === 'delivery' ? deliveryAddress : null,
        couponCode: appliedCoupon ? couponCode.trim() : null,
        notes,
        scheduledTime: scheduledType === 'asap' ? 'asap' : scheduledTime,
        guestName: !user ? guestInfo.name : null,
        guestEmail: !user ? guestInfo.email : null,
        guestPhone: guestInfo.phone || user?.phone || null,
      };
      const order = await api.createOrder(orderData);
      setCurrentOrder({ ...order, estimatedTime: activeTab === 'pickup' ? '15 - 20 mins' : '30 - 45 mins' });


      if (paymentMethod === 'cash') {
        setIsPlacingOrder(true);
        // Clear form data on successful cash order
        clearCart();
        localStorage.removeItem('checkoutFormData');
        sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
        navigate(`/order-status/${order.id}`);
      } else {
        // Create Stripe Checkout Session and redirect
        const checkoutSession = await api.createCheckoutSession(order.id);
        if (checkoutSession.mock) {
          setIsPlacingOrder(true);   // ✅ ADD THIS
          // Mock flow for testing without Stripe keys
          clearCart();
          localStorage.removeItem('checkoutFormData');
          sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
          navigate(`/order-status/${order.id}?payment=success&mock=true`);
        } else {
          // Redirect to Stripe Checkout (form data saved in localStorage)
          window.location.href = checkoutSession.url;
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  if (cart.length === 0) {
  // If user previously had a cart → don't render anything (redirect will happen via useEffect)
  if (hadCartBefore.current) {
    return null;
  }

  // Normal empty basket UI
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center p-8">
        <div className="text-6xl mb-4">🛒</div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Your basket is empty</h2>
        <p className="text-gray-500 mb-6">Add items to get started</p>
        <button
          onClick={() => navigate('/')}
          className="bg-gray-900 text-white px-8 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200"
        >
          Browse Menu
        </button>
      </div>
    </div>
  );
}

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white sm:bg-gray-50 font-sans">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-[40px_1fr_40px] items-center">
            <span aria-hidden="true" />
            <h1 className="text-lg font-bold text-gray-900 text-center">Checkout</h1>
            <span aria-hidden="true" />
          </div>
        </div>
        <div className="min-h-[45vh] flex items-center justify-center" role="status" aria-label="Loading checkout">
          <Loader className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!user && checkoutMode !== 'guest') {
    return (
      <div className="min-h-screen bg-white sm:bg-gray-50 font-sans">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-4 py-4 grid grid-cols-[40px_1fr_40px] items-center">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Back to menu">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-lg font-bold text-gray-900 text-center">Checkout</h1>
            <span aria-hidden="true" />
          </div>
        </div>
        <main className="max-w-lg mx-auto px-4 py-10 sm:py-16">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8">
            <div className="w-12 h-12 rounded-2xl bg-gray-900 text-white flex items-center justify-center mb-5">
              <User className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">How would you like to continue?</h2>
            <p className="text-sm text-gray-500 mt-2 mb-6">Sign in for a faster checkout with your saved details, or continue without an account.</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login', { state: { returnTo: '/checkout' } })}
                className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-semibold hover:bg-amber-700 transition-colors">
                Sign in or create an account
              </button>
              <button
                onClick={() => { sessionStorage.setItem(CHECKOUT_MODE_KEY, 'guest'); setCheckoutMode('guest'); }}
                className="w-full bg-white text-gray-900 py-3.5 rounded-xl font-semibold border border-gray-300 hover:bg-gray-50 transition-colors">
                Continue as guest
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-5">Your basket will be kept while you sign in.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-white sm:bg-gray-50 font-sans">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-[40px_1fr_40px] items-center">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900 text-center">Checkout</h1>
          <span aria-hidden="true" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-0 py-0 sm:px-4 sm:py-6">

        {/* Payment Cancelled Message */}
        {paymentCancelled && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <X className="w-3 h-3 text-amber-800" />
              </div>
              <div className="flex-1">
                <p className="text-amber-900 font-semibold text-sm mb-1">Payment Cancelled</p>
                <p className="text-amber-800 text-xs">You cancelled the payment on Stripe. You can make changes to your order below and try again, or choose a different payment method.</p>
              </div>
              <button onClick={() => setPaymentCancelled(false)} className="text-amber-600 hover:text-amber-800">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-6 lg:items-start">
        <div className="space-y-0 sm:space-y-4 min-w-0 bg-white sm:bg-transparent">
        {/* Order Type */}
        <div className={checkoutSectionClass}>
          <div className={`${checkoutHeaderClass} hidden sm:block`}><h3 className="font-semibold text-gray-900">Order type</h3></div>
          <div className={checkoutBodyClass}>
            <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl">
              {['pickup', 'delivery'].map(t => (
                <button key={t} onClick={() => setActiveTab(t)}
                  className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 capitalize ${activeTab === t ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* When to Receive */}
        <div className={checkoutSectionClass}>
          <div className={`${checkoutHeaderClass} flex items-center gap-2`}>
            <Clock className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-gray-900">When would you like your order?</h3>
          </div>
          <div className={`${checkoutBodyClass} space-y-3`}>
            <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl">
              <button onClick={() => setScheduledType('asap')}
                className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${scheduledType === 'asap' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                ASAP
              </button>
              <button onClick={() => setScheduledType('later')}
                className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${scheduledType === 'later' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                Schedule Later
              </button>
            </div>
            {scheduledType === 'asap' && (
              <p className="text-sm text-gray-500 text-center">
                Estimated {activeTab === 'pickup' ? (restaurant?.pickupTime || '15-20 Mins') : (restaurant?.deliveryTime || '25-40 Mins')}
              </p>
            )}
            {scheduledType === 'later' && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Select a time</label>
                <select value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white">
                  <option value="">-- Choose a time --</option>
                  {timeSlots.length === 0 ? (
                    <option value="" disabled>No available times today</option>
                  ) : (
                    timeSlots.map(t => <option key={t} value={t}>{t}</option>)
                  )}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Address */}
        {activeTab === 'delivery' && (
          <div className={checkoutSectionClass}>
            <div className={`${checkoutHeaderClass} flex items-center gap-2`}>
              <MapPin className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold text-gray-900">Delivery Address</h3>
            </div>
            <div className={`${checkoutBodyClass} space-y-3`}>
              {user && savedAddressesLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <Loader className="w-4 h-4 animate-spin" /> Loading saved addresses…
                </div>
              )}

              {user && selectedAddressId && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900">{deliveryAddress.line1}</p>
                    {deliveryAddress.line2 && <p className="text-sm text-gray-500">{deliveryAddress.line2}</p>}
                    <p className="text-sm text-gray-500">{deliveryAddress.city}, {deliveryAddress.postcode}</p>
                    {deliveryFeeInfo?.deliverable && <p className="text-xs text-green-700 mt-1">Delivery available · £{deliveryFeeInfo.fee?.toFixed(2)}</p>}
                  </div>
                  <button onClick={() => { setSelectedAddressId(null); setAddressEditorOpen(false); }} className="text-sm font-semibold text-amber-700 hover:text-amber-800">Change</button>
                </div>
              )}
              {user && selectedAddressId && postcodeError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{postcodeError}</p>
              )}

              {user && !selectedAddressId && savedAddresses.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Choose a saved address</p>
                  {savedAddresses.map(address => (
                    <div key={address.id} className="w-full rounded-xl border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-colors flex items-start">
                      <button onClick={() => selectSavedAddress(address)} className="flex-1 min-w-0 text-left p-3 flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-gray-900">{address.label || 'Saved address'}</span>
                          <span className="block text-xs text-gray-500 mt-0.5">{address.line1}{address.line2 ? `, ${address.line2}` : ''}, {address.city}, {address.postcode}</span>
                        </span>
                      </button>
                      <button onClick={event => removeSavedAddress(event, address.id)} aria-label="Remove saved address"
                        className="m-2 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={startNewAddress} className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 pt-1">
                    <Plus className="w-4 h-4" /> Add a new address
                  </button>
                </div>
              )}

              {!selectedAddressId && (savedAddresses.length === 0 || addressEditorOpen) && (!manualEntry ? (
                <>
                  {/* Postcode Search */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">Enter your postcode to find your address</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g. TS1 4AG"
                        value={postcodeInput}
                        onChange={e => { setPostcodeInput(e.target.value.toUpperCase()); setPostcodeError(''); }}
                        onKeyDown={e => e.key === 'Enter' && handlePostcodeSearch()}
                        className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors font-mono"
                      />
                      <button onClick={handlePostcodeSearch} disabled={postcodeSearching}
                        className="px-4 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-amber-700 transition-colors disabled:opacity-60 flex items-center gap-2">
                        {postcodeSearching ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        <span className="hidden sm:inline">Find</span>
                      </button>
                    </div>
                    {postcodeError && <p className="text-red-500 text-xs mt-1.5">{postcodeError}</p>}
                  </div>

                  {/* Postcode Result - Real Addresses */}
                  {postcodeResult && !postcodeError && postcodeResult.addresses && postcodeResult.addresses.length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-600">Select your address</span>
                        <button onClick={() => setPostcodeResult(null)} className="text-gray-400 hover:text-gray-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                        {postcodeResult.addresses.map((addr, i) => (
                          <button key={i} onClick={() => {
                            setDeliveryAddress({
                              line1: addr.line1,
                              line2: addr.line2 || '',
                              city: addr.town || postcodeResult.town || '',
                              postcode: addr.postcode || postcodeResult.postcode,
                              latitude: addr.latitude ?? postcodeResult.latitude,
                              longitude: addr.longitude ?? postcodeResult.longitude
                            });
                            setPostcodeResult(null);
                            setManualEntry(true); // Show the filled fields
                          }}
                            className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-800 transition-colors flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span>{addr.formatted}</span>
                          </button>
                        ))}
                      </div>
                      <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-center">
                        <button onClick={() => setPostcodeResult(null)} className="text-xs text-amber-700 font-semibold hover:text-amber-800">
                          Enter address manually
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Postcode Result - No Addresses Found */}
                  {postcodeResult && !postcodeError && (!postcodeResult.addresses || postcodeResult.addresses.length === 0) && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-green-800 font-semibold text-sm mb-1">✓ Postcode Verified: {postcodeResult.postcode}</p>
                          <p className="text-green-700 text-xs mb-1">Town: {postcodeResult.town}</p>
                          {deliveryFeeInfo && <p className="text-green-700 text-xs mb-2">Delivery Fee: £{deliveryFeeInfo.fee?.toFixed(2)}</p>}
                          <p className="text-gray-600 text-xs mt-2 bg-white/50 rounded px-2 py-1.5 border border-green-200">
                            ℹ️ Please enter your full street address in the fields below
                          </p>
                        </div>
                        <button onClick={() => setPostcodeResult(null)} className="text-green-600 hover:text-green-800">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Populated address after selection */}
                  {deliveryAddress.line1 && !postcodeResult && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-green-800">{deliveryAddress.line1}</p>
                          <p className="text-xs text-green-700">{deliveryAddress.city}, {deliveryAddress.postcode}</p>
                        </div>
                        <button onClick={() => setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' })}
                          className="text-green-600 hover:text-green-800 text-xs font-semibold">Change</button>
                      </div>
                      {deliveryFeeInfo && (
                        <p className="text-xs text-green-600 mt-1.5 ml-6">
                          {deliveryFeeInfo.zoneName && `Zone: ${deliveryFeeInfo.zoneName} · `}
                          Delivery fee: £{deliveryFeeInfo.fee?.toFixed(2)}
                        </p>
                      )}
                      {/* Allow adding line 2 */}
                      <div className="mt-2 ml-6">
                        <input type="text" placeholder="Flat/Apartment number (optional)" value={deliveryAddress.line2}
                          onChange={e => setDeliveryAddress(p => ({ ...p, line2: e.target.value }))}
                          className="w-full border border-green-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-amber-500 bg-white" />
                      </div>
                    </div>
                  )}

                  {!deliveryAddress.line1 && !postcodeResult && (
                    <button onClick={() => setManualEntry(true)} className="text-xs text-amber-700 font-semibold hover:text-amber-800">
                      Enter address manually
                    </button>
                  )}
                </>
              ) : (
                // Manual entry
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-600">Manual Address Entry</span>
                    <button onClick={() => setManualEntry(false)} className="text-xs text-amber-700 font-semibold hover:text-amber-800">
                      Use postcode search
                    </button>
                  </div>
                  <input type="text" placeholder="Address line 1 *" value={deliveryAddress.line1}
                    onChange={e => setDeliveryAddress(p => ({ ...p, line1: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
                  <input type="text" placeholder="Address line 2 (optional)" value={deliveryAddress.line2}
                    onChange={e => setDeliveryAddress(p => ({ ...p, line2: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder="City/Town" value={deliveryAddress.city}
                      onChange={e => setDeliveryAddress(p => ({ ...p, city: e.target.value }))}
                      className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
                    <div className="relative">
                      <input type="text" placeholder="Postcode *" value={deliveryAddress.postcode}
                        onChange={e => {
                          setDeliveryAddress(p => ({ ...p, postcode: e.target.value.toUpperCase() }));
                          setPostcodeError('');
                        }}
                        onBlur={async () => {
                          // Auto-verify postcode on blur if entered manually
                          if (deliveryAddress.postcode && manualEntry && activeTab === 'delivery') {
                            try {
                              const zoneCheck = await api.checkDeliveryZone(deliveryAddress.postcode);
                              setDeliveryFeeInfo(zoneCheck);

                              setPostcodeError(''); // ✅ CLEAR ERROR ON SUCCESS

                              if (!zoneCheck.deliverable) {
                                setPostcodeError('We do not deliver to this postcode. Please choose Pickup or enter a different postcode.');
                                setDeliveryAddress({
                                  line1: '',
                                  line2: '',
                                  city: '',
                                  postcode: ''
                                });
                                setDeliveryFeeInfo(null);
                              }
                            } catch (e) {
                              // Use default fee if no zones configured
                              setDeliveryFeeInfo({ deliverable: true, fee: defaultDeliveryFee });
                            }
                          }
                        }}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors font-mono" />
                    </div>
                    {postcodeError && (
                      <p className="text-red-500 text-xs mt-1.5">
                        {postcodeError}
                      </p>
                    )}
                  </div>
                  {deliveryAddress.postcode && deliveryFeeInfo && deliveryFeeInfo.deliverable && (
                    <div className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2 flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Postcode verified · Delivery fee: £{deliveryFeeInfo.fee?.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Info (guest) */}
        {!user ? (
          <div className={checkoutSectionClass}>
            <div className={`${checkoutHeaderClass} flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-gray-900">Contact Details</h3>
              </div>
              <button onClick={() => navigate('/login', { state: { returnTo: '/checkout' } })}
                className="text-sm text-amber-700 font-semibold hover:text-amber-800">Sign in</button>
            </div>
            <div className={`${checkoutBodyClass} space-y-3`}>
              {[{key: 'name', ph: 'Full name *', type: 'text'}, {key: 'email', ph: 'Email address *', type: 'email'}, {key: 'phone', ph: 'Phone number *', type: 'tel'}].map(f => (
                <input key={f.key} type={f.type} placeholder={f.ph} value={guestInfo[f.key]}
                  onChange={e => setGuestInfo(p => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
              ))}
            </div>
          </div>
        ) : (
          <div className={checkoutSectionClass}>
            <div className="px-5 py-5 sm:p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
              <span className="text-xs font-medium text-green-700 bg-green-50 rounded-full px-2.5 py-1">Signed in</span>
            </div>
          </div>
        )}

        {/* Payment Method */}
        <div className={checkoutSectionClass}>
          <div className={`${checkoutHeaderClass} flex items-center gap-2`}>
            <CreditCard className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Payment Method</h3>
          </div>
          <div className={`${checkoutBodyClass} space-y-3`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 sm:gap-3 border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-200 sm:border-0 sm:rounded-none sm:overflow-visible sm:divide-y-0">
              <button onClick={() => setPaymentMethod('card')}
                className={`flex items-center gap-3 p-4 border-0 sm:rounded-xl sm:border-2 transition-all duration-200 ${paymentMethod === 'card' ? 'sm:border-amber-500 bg-amber-50' : 'sm:border-gray-200 hover:bg-gray-50 sm:hover:border-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'card' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}`}>
                  {paymentMethod === 'card' && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                </div>
                <div className="text-left">
                  <p className={`text-sm font-semibold ${paymentMethod === 'card' ? 'text-amber-800' : 'text-gray-700'}`}>Pay by Card</p>
                  <p className="text-xs text-gray-400">Debit, Credit Card, Apple Pay & Google Pay</p>
                </div>
              </button>
              <button onClick={() => setPaymentMethod('cash')}
                className={`flex items-center gap-3 p-4 border-0 sm:rounded-xl sm:border-2 transition-all duration-200 ${paymentMethod === 'cash' ? 'sm:border-amber-500 bg-amber-50' : 'sm:border-gray-200 hover:bg-gray-50 sm:hover:border-gray-300'}`}>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'cash' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}`}>
                  {paymentMethod === 'cash' && <div className="w-2.5 h-2.5 bg-white rounded-full" />}
                </div>
                <div className="text-left">
                  <p className={`text-sm font-semibold ${paymentMethod === 'cash' ? 'text-amber-800' : 'text-gray-700'}`}>
                    {activeTab === 'delivery' ? 'Cash on Delivery' : 'Cash on Collection'}
                  </p>
                  <p className="text-xs text-gray-400">Pay on collection/delivery</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className={checkoutSectionClass}>
          <div className={checkoutHeaderClass}><h3 className="font-semibold text-gray-900">Order notes</h3></div>
          <div className={checkoutBodyClass}>
            <textarea placeholder="Any special requests or allergies? (optional)" value={notes} onChange={e => setNotes(e.target.value)}
              rows={3} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors resize-none" />
          </div>
        </div>

        </div>
        <aside className="space-y-0 mt-0 border-t-8 border-gray-100 sm:space-y-4 sm:mt-4 sm:border-t-0 lg:mt-0 lg:sticky lg:top-24">
        {/* Coupon */}
        <div className={checkoutSectionClass}>
          <div className={`${checkoutHeaderClass} flex items-center gap-2`}>
            <Tag className="w-4 h-4 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Coupon Code</h3>
          </div>
          <div className={checkoutBodyClass}>
            {appliedCoupon ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-green-800 text-sm">{appliedCoupon.code}</p>
                    <p className="text-green-600 text-xs">
                      {appliedCoupon.description || (appliedCoupon.type === 'free_delivery' ? 'Free delivery' : 'Coupon applied')}
                      {' · '}
                      {appliedCoupon.type === 'free_delivery' ? 'Delivery fee removed' : `−£${appliedCoupon.discount.toFixed(2)}`}
                    </p>
                  </div>
                  <button onClick={() => { setAppliedCoupon(null); setCouponCode(''); }} className="text-green-700 text-xs font-semibold hover:text-green-900">Remove</button>
                </div>
                {appliedCoupon.bogoItems && appliedCoupon.bogoItems.length > 0 && (
                  <div className="pt-2 border-t border-green-200">
                    <p className="text-green-700 text-xs font-semibold mb-1">
                      🎉 {appliedCoupon.type === 'bogo' ? 'FREE' : '50% OFF'}:
                    </p>
                    {appliedCoupon.bogoItems.map((item, i) => (
                      <p key={i} className="text-green-600 text-xs">• {item}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" placeholder="Enter coupon code" value={couponCode}
                  onChange={e => { setCouponCode(e.target.value); setCouponError(''); }}
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
                <button onClick={handleApplyCoupon} disabled={couponLoading}
                  className="px-5 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-amber-700 transition-colors duration-200 disabled:opacity-60">
                  {couponLoading ? '...' : 'Apply'}
                </button>
              </div>
            )}
            {couponError && <p className="text-red-500 text-xs mt-2">{couponError}</p>}
          </div>
        </div>

        {/* Order Summary */}
        <div className={checkoutSectionClass}>
          <div className={checkoutHeaderClass}><h3 className="font-semibold text-gray-900">Order summary</h3></div>
          <div className={`${checkoutBodyClass} space-y-3`}>
            {cart.map(item => (
              <div key={item.cartItemId} className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="w-6 h-6 bg-amber-100 text-amber-800 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0">{item.quantity}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-800 block">{item.name}</span>
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <span className="text-xs text-gray-500 block mt-0.5">
                        {Object.entries(item.selectedOptions).map(([optId, choice]) => {
                          if (Array.isArray(choice)) {
                            return choice.map(c => c.name).join(', ');
                          }
                          return choice?.name || '';
                        }).filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 flex-shrink-0">£{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>£{cartSubtotal.toFixed(2)}</span></div>
              {activeTab === 'delivery' && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery fee</span>
                  <span>£{currentDeliveryFee.toFixed(2)}</span>
                </div>
              )}
              {serviceCharge > 0 && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Service charge</span>
                  <span>£{serviceCharge.toFixed(2)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-600 font-medium">
                  <span>{appliedCoupon?.type === 'free_delivery' ? 'Free delivery' : 'Discount'} ({appliedCoupon?.code})</span>
                  <span>−£{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-100">
                <span>Total</span><span>£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="mx-5 sm:mx-0 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

        <div className="px-5 py-4 sm:p-0 space-y-2">
          <button onClick={handlePlaceOrder} disabled={loading}
            className="w-full bg-gray-900 text-white py-4 rounded-xl sm:rounded-2xl font-bold text-base hover:bg-amber-700 transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
            {paymentMethod === 'cash' ? <Banknote className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {loading ? 'Processing...' : paymentMethod === 'cash'
              ? `Place Order · £${total.toFixed(2)}`
              : `Proceed to Pay · £${total.toFixed(2)}`
            }
          </button>

          {paymentMethod === 'cash' && (
            <p className="text-center text-xs text-gray-400">Please have exact change ready</p>
          )}
          {paymentMethod === 'card' && (
            <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" />Secured by Stripe
            </p>
          )}
        </div>
        </aside>
        </div>
      </div>
    </div>
 
);
}
